/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\BackupJobConfigBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { Database } from 'lucide-react';
import { FormBox } from '../../../../components/ui/FormBox';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Toggle } from '../../../../components/ui/Toggle';
import SimpleTooltip from '../../../../components/ui/SimpleTooltip';
import TenantSelect from '../../../../components/ui/TenantSelect';
import { useI18n } from '../../../../hooks/useI18n';

const SELECT_CLS = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

type Tenant = { id: number | string; name: string; slug?: string; telephonyId?: number | string };
type CronProfile = { id: number | string; name: string; schedule?: string };

interface FormData {
    name: string;
    tenantScope: string;
    tenantId: string;
    cronProfileId: string;
    limit: number;
    includeCdr: boolean;
    includeRecordings: boolean;
    includeVoicemails: boolean;
    includeFaxes: boolean;
}

interface Props {
    formData: FormData;
    tenants: Tenant[];
    cronProfiles: CronProfile[];
    onChange: (key: string, value: string | number | boolean) => void;
    onTenantChange: (val: number | undefined) => void;
}

export function BackupJobConfigBox({ formData, tenants, cronProfiles, onChange, onTenantChange }: Props) {
    const { t } = useI18n();

    return (
        <FormBox title={t('backupRestore:sections.backupConfig')} description={t('backupRestore:sections.backupConfigDesc')} icon={<Database className="h-5 w-5 text-primary" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                {/* Job Name | Include CDR */}
                <SimpleTooltip content={t('backupRestore:tooltips.jobName')}><Label>{t('backupRestore:fields.jobName')}</Label></SimpleTooltip>
                <Input value={formData.name} onChange={(e) => onChange('name', e.target.value)} placeholder={t('backupRestore:fields.jobNamePlaceholder')} />
                <SimpleTooltip content={t('backupRestore:tooltips.includeCdr')}><Label htmlFor="cdr" className="cursor-pointer">{t('backupRestore:fields.includeCdr')}</Label></SimpleTooltip>
                <div className="flex justify-end pr-2"><Toggle id="cdr" value={formData.includeCdr} onChange={(c) => onChange('includeCdr', c)} /></div>

                {/* Tenant | Include Recordings */}
                <SimpleTooltip content={t('backupRestore:tooltips.selectTenant')}><Label>{t('backupRestore:fields.selectTenant')}</Label></SimpleTooltip>
                <TenantSelect
                    value={formData.tenantScope === 'all' ? -1 : (formData.tenantId ? parseInt(formData.tenantId) : undefined)}
                    onChange={onTenantChange}
                    tenants={tenants as unknown as Parameters<typeof TenantSelect>[0]['tenants']}
                    placeholder={t('backupRestore:fields.selectScope')}
                />
                <SimpleTooltip content={t('backupRestore:tooltips.includeRecordings')}><Label htmlFor="rec" className="cursor-pointer">{t('backupRestore:fields.includeRecordings')}</Label></SimpleTooltip>
                <div className="flex justify-end pr-2"><Toggle id="rec" value={formData.includeRecordings} onChange={(c) => onChange('includeRecordings', c)} /></div>

                {/* Cron | Include Voicemails */}
                <SimpleTooltip content={t('backupRestore:tooltips.runAutomatically')}><Label>{t('backupRestore:fields.runAutomatically')}</Label></SimpleTooltip>
                <select className={SELECT_CLS} value={formData.cronProfileId} onChange={(e) => onChange('cronProfileId', e.target.value)}>
                    <option value="disabled">{t('backupRestore:fields.disabled')}</option>
                    {cronProfiles.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.schedule})</option>)}
                </select>
                <SimpleTooltip content={t('backupRestore:tooltips.includeVoicemails')}><Label htmlFor="vm" className="cursor-pointer">{t('backupRestore:fields.includeVoicemails')}</Label></SimpleTooltip>
                <div className="flex justify-end pr-2"><Toggle id="vm" value={formData.includeVoicemails} onChange={(c) => onChange('includeVoicemails', c)} /></div>

                {/* Limit | Include Faxes */}
                <SimpleTooltip content={t('backupRestore:tooltips.retentionLimit')}><Label>{t('backupRestore:fields.retentionLimit')}</Label></SimpleTooltip>
                <Input type="number" min={1} max={100} value={formData.limit} onChange={(e) => onChange('limit', parseInt(e.target.value) || 1)} />
                <SimpleTooltip content={t('backupRestore:tooltips.includeFaxes')}><Label htmlFor="fax" className="cursor-pointer">{t('backupRestore:fields.includeFaxes')}</Label></SimpleTooltip>
                <div className="flex justify-end pr-2"><Toggle id="fax" value={formData.includeFaxes} onChange={(c) => onChange('includeFaxes', c)} /></div>
            </div>
        </FormBox>
    );
}
